import {Api} from "laravel-request";

import {downloadExcelFile} from "#app/shared/lib/network/urlHelper";

import type { ApiResponse } from "../types/api.types";
import {
    ClientDict,
    CompressedCrmReport,
    DecompressedCrmReport,
    DetailedCell, DetailedTable,
    MonthKey, ServiceAggregatedRow, ServiceAton, ServiceAtonStatus, ServiceDict,
    ServiceRow, ServicesAggregated, ServiceTable,
    TotalRow, TotalsTable
} from "../types/contracts";
import type { LaravelQueryBuilder } from "../types/laravelRequest";
import type { ApiSuccessHandler } from "../types/legacyCallbacks";

type Service = {
    id: number;
    name?: string;
    clients?: any[];
};

type PaidServiceRecord = {
    id?: number;
    is_paid?: boolean | number | string;
    data?: { id?: number; is_paid?: boolean | number | string };
    dataEntries?: PaidServiceRecord[];
};

/**
 * Laravel часто отдаёт boolean-поля как 0/1 либо "0"/"1" (строка), а не нативный JS boolean.
 * `Boolean("0")` даёт `true` — этой ловушки быть не должно, поэтому нормализуем явно.
 */
export function toIsPaidBoolean(value: unknown): boolean {
    if (typeof value === 'string') {
        return value !== '' && value !== '0';
    }
    return Boolean(value);
}

/**
 * Чистый расчёт payload для смены статуса "оплачено" — без сетевого вызова.
 * Вынесен отдельно, чтобы вызывающий код (см. очередь операций в ClientViews)
 * мог посчитать целевое значение один раз, в момент клика, и переиспользовать
 * этот же зафиксированный payload при повторной отправке (после reload), не
 * пересчитывая "инверсию текущего" заново по потенциально устаревшим данным.
 * @param rows сущности, подлежащие обновлению (массив или одна запись)
 */
export function computePaidTogglePayload(
    rows: PaidServiceRecord | PaidServiceRecord[],
    nextIsPaid?: boolean
): { ids: Array<number | string>; payload: Record<string, any> } | null {
    const entries = Array.isArray(rows) ? rows : [rows];
    const warnInConsole = (entry: any) => console.warn("Отсутствует id у записи", entry);

    const allRecords = entries.reduce<PaidServiceRecord[]>((accumulator, row) => {
        if (row.dataEntries && Array.isArray(row.dataEntries)) {
            accumulator.push(...row.dataEntries);
        } else {
            accumulator.push(row);
        }
        return accumulator;
    }, []);

    const ids: Array<number | string> = [];
    const isPaidMap: Record<number | string, boolean> = {};

    for (const record of allRecords) {
        const id = record.id ?? record.data?.id;
        if (!id) {
            warnInConsole(record);
            continue;
        }

        const isPaid = record.is_paid ?? record.data?.is_paid;
        ids.push(id);
        isPaidMap[id] = toIsPaidBoolean(isPaid);
    }

    if (ids.length === 0) {
        console.warn("Нет записей для обновления");
        return null;
    }

    const allRowsPaid = Object.values(isPaidMap).every(Boolean);
    const targetIsPaid = typeof nextIsPaid === 'boolean' ? nextIsPaid : !allRowsPaid;
    let payload = {is_paid: targetIsPaid ? 1 : 0};
    if (ids.length > 1) {
        payload = {...payload, ...{ids: ids}};
    }

    return {ids, payload};
}

/**
 * Отправка уже посчитанного (зафиксированного) payload — без пересчёта "инверсии
 * текущего". Используется как для первичной отправки, так и для гарантированного
 * повтора после reload (см. servicesRequestQueue.ts) — оба раза уходит один и тот
 * же payload, поэтому повтор идемпотентен.
 */
export const putServicesPaid = (
    ids: Array<number | string>,
    payload: Record<string, any>,
    callbackOnPaidChange: (data: any) => void,
    callbackOnError?: (xhr: XMLHttpRequest, responseData: any) => void,
    callbackOnFinally?: () => void
) => Api.putArg('crm-services', 'paid', ids[0], payload).call(
    () => {
        if (typeof callbackOnPaidChange === 'function') {
            callbackOnPaidChange(payload);
        }
    },
    callbackOnError,
    callbackOnFinally
);

/**
 * Ручка для смены статуса "оплачено" у услуги
 * @param rows сущности, подлежащие обновлению (массив или одна запись)
 * @param callbackOnPaidChange
 * @param nextIsPaid целевое значение "оплачено" (true/false), без инверсий
 * @param callbackOnError вызывается при ошибке запроса — без него сбой проходил тихо
 * @param callbackOnFinally вызывается после успеха ИЛИ ошибки — удобно снимать pending-состояние в UI
 */
export const crmServicesPutPaid = (
    rows: PaidServiceRecord | PaidServiceRecord[],
    callbackOnPaidChange: (data: any) => void,
    nextIsPaid?: boolean,
    callbackOnError?: (xhr: XMLHttpRequest, responseData: any) => void,
    callbackOnFinally?: () => void
) => {
    const computed = computePaidTogglePayload(rows, nextIsPaid);
    if (!computed) {
        callbackOnFinally?.();
        return Promise.resolve();
    }
    return putServicesPaid(computed.ids, computed.payload, callbackOnPaidChange, callbackOnError, callbackOnFinally);
};

/**
 * Ручка для привязки клиенту
 * @param valuationsList
 * @param apiParams
 * @param callbackOnAssign
 * @param callbackOnResponse необязательный колбэк с сырым телом ответа (например, чтобы
 * достать `meta.data.job_id` для async: true) — вызывается, только если result === 'success'
 * (см. комментарий у finalCallback ниже), поэтому НЕ используется для сигнала "запрос
 * завершился"; для этого по-прежнему служит callbackOnAssign/finalCallback.
 */
export const assignServices = (
    valuationsList: null,
    apiParams: Record<string, any> | undefined,
    callbackOnAssign: () => void,
    callbackOnResponse?: (response: any) => void
) => Api.post(
    'crm-services', 'assign', valuationsList
        ? {...apiParams, ...{valuations: valuationsList}}
        : apiParams
).call(
    (response: any) => {
        if (typeof callbackOnResponse === 'function') {
            callbackOnResponse(response);
        }
    },
    (_xhr: any, responseData: any) => {
        console.error('Ошибка при привязке услуги:', responseData);
    },
    () => callbackOnAssign && callbackOnAssign()
);

export const calculateSuccessFee = (
    clientId: number | string,
    callbackOnSuccess: () => void
) => Api.post('crm-services', 'calculate-success', {
    client_id: clientId
}).call(
    undefined,
    (_xhr: any, responseData: any) => {
        console.error('Ошибка при расчёте платы за успех:', responseData);
    },
    () => callbackOnSuccess && callbackOnSuccess()
);

/**
 * Ручка для получения всех сервисов по определенному клиенту
 * @param clientId
 * @param callbackOnGetServices
 */
export const getServices = (
    clientId: number | string,
    callbackOnGetServices: (data: any) => void
) => {
    Api.get('crm-services', 'index')
        .with('clients', (query: LaravelQueryBuilder) =>
            query.orderBy('created_at').where('client_id', '=', clientId)
        ).all(
        (response: ApiResponse<any>) => callbackOnGetServices(response.data)
    );
};

export const getAllServices = (
    callback: (data: Service[]) => void,
    isArchived: boolean | null = null
) => {
    const query = Api.get('crm-services', 'index')
        .with('clients', (clientsQuery: LaravelQueryBuilder) =>
            clientsQuery
                .with('client', (clientQuery: LaravelQueryBuilder) => clientQuery.with('mainContact'))
                .with('payments', (paymentsQuery: LaravelQueryBuilder) => paymentsQuery.with('tariff'))
        );

    if (isArchived !== null) {
        query.where('is_archived', isArchived);
    }

    query.all(
        (response) => callback(response.data)
    );
};

export const getAllServicesCompressed = (
    userId: number | string,
    callback: (data: DecompressedCrmReport) => void,
    isArchived: boolean | null = null,
    onError?: (xhr: XMLHttpRequest, responseData: any) => void
) => {
    Api.get('crm-services', 'table-report', {
        is_archived: isArchived,
        responsible_id: userId,
    }).all(
        (response) => {
            const decompressedResponse = decompressCrmReport(
                response.data
            );
            callback(decompressedResponse);
        },
        onError
    )
}

export const setServiceToUpdate = (
    id: number,
    isArchived: boolean,
    callbackOnSucces: (response: ApiResponse<any>) => void
) => {
    Api.putArg('crm-services', 'update', id, {is_archived: isArchived}).call((response: ApiResponse<any>) => callbackOnSucces(response));
};

export const generateExcelFileByServiceData = (
    data: Record<string, any>,
    clientName: string,
    serviceName: string,
    callbackOnSuccess: (response: ApiResponse<any>) => void
) => {
    Api.post('crm-services', 'generate-excel', {
        data: data, client: clientName, service: serviceName
    }).call(
        (response) => callbackOnSuccess && callbackOnSuccess(response)
    );
}

export const decompressCrmReport = (compressed: CompressedCrmReport): DecompressedCrmReport => {
    const isArray = Array.isArray; // алиас, чтобы не дублировать каждый раз
    const convertValueToACertainString = (numberOrString: string | number): number => {
        const value = typeof numberOrString === "number" ? numberOrString : parseInt(numberOrString, 10);
        return Number.isFinite(value) ? value : 0;
    };

    const coerceRow = (row: any): ServiceRow | TotalRow => {
        if (!isArray(row)) return [0, 0, 0, []];
        const [amount, isPaid, isArchived, ids] = row as any[];
        return [
            typeof amount === "number" ? amount : 0,
            typeof isPaid === "number" ? isPaid : Number(Boolean(isPaid)),
            typeof isArchived === "number" ? isArchived : Number(Boolean(isArchived)),
            isArray(ids) ? ids : [],
        ];
    }

    const createEmptyCell = (): DetailedCell => ({
        amount: 0,
        is_paid: false,
        is_archived: false,
        ids: [],
        is_paid_list: [],
        is_archived_list: [],
    });

    const monthKeysDesc: MonthKey[] = isArray(compressed.m) ? (compressed.m as MonthKey[]) : [];
    const clientDict: ClientDict = (compressed.c ?? {}) as ClientDict;
    const serviceDict: ServiceDict = (compressed.s ?? {}) as ServiceDict;
    const _activeClientIds: number[] = (compressed.a ?? []).map(convertValueToACertainString);
    const archivedClientIds: number[] = (compressed.r ?? []).map(convertValueToACertainString);
    const totalTable: TotalsTable = (compressed.t ?? {}) as TotalsTable;
    const serviceTable: ServiceTable = (compressed.v ?? {}) as ServiceTable;
    const serviceAton: ServiceAton = (compressed.va ?? {}) as ServiceAton;
    const serviceAtonStatus: ServiceAtonStatus = (compressed.vas ?? {}) as ServiceAtonStatus;

    const clientDirectory: DecompressedCrmReport["clientDirectory"] = {};

    for (const [idStringified, valuesList] of Object.entries(clientDict)) {
        const clientId = convertValueToACertainString(idStringified);
        const first = isArray(valuesList) && typeof valuesList[0] === "string" ? valuesList[0] : "";
        const last = isArray(valuesList) && typeof valuesList[1] === "string" ? valuesList[1] : "";
        clientDirectory[clientId] = {id: clientId, first_name: first, last_name: last};
    }

    const archivedByClientId: Record<number, boolean> = {};
    for (const idStringified of Object.keys(clientDict)) {
        const id = convertValueToACertainString(idStringified);
        archivedByClientId[id] = archivedClientIds.includes(id);
    }

    const buildDetailedTableForClients = (
        clients: Array<{ id: number; first_name: string; last_name: string }>
    ): DetailedTable => {
        const table: DetailedTable = {};

        clients.forEach((client) => {
            const clientId = client.id;
            const printableName = (client.last_name.trim() + " " + client.first_name.trim()).trim() || `Клиент ${clientId}`;
            const totalCellsByMonth: Record<string, DetailedCell> = {};
            for (const monthKey of monthKeysDesc) totalCellsByMonth[monthKey] = createEmptyCell();

            const clientTotalData = (totalTable[String(clientId)] ?? totalTable[clientId] ?? []) as any[];

            for (let i = 0; i < monthKeysDesc.length; i++) {
                const monthKey = monthKeysDesc[i];
                const [amount, isPaid, isArchived, ids] = coerceRow(clientTotalData[i]);
                const cell = totalCellsByMonth[monthKey];
                cell.amount = amount;
                cell.is_paid = Boolean(isPaid);
                cell.is_archived = Boolean(isArchived);
                cell.ids = (ids as Array<number | string>).map(convertValueToACertainString);
                cell.is_paid_list = [Boolean(isPaid)];
                cell.is_archived_list = [Boolean(isArchived)];
            }

            const servicesAggregated: ServicesAggregated = {};
            const clientServices = (serviceTable[String(clientId)] ?? serviceTable[clientId] ?? {}) as Record<string, any>;

            for (const [serviceIdStringified, monthlyArrayUnknown] of Object.entries(clientServices)) {
                const serviceId = convertValueToACertainString(serviceIdStringified);
                const serviceName = serviceDict[String(serviceId)] ?? serviceDict[serviceId] ?? `Услуга ${serviceId}`;

                const monthlyCellsByService: Record<string, DetailedCell> = {};
                for (const monthKeyEntry of monthKeysDesc) monthlyCellsByService[monthKeyEntry] = createEmptyCell();

                const monthlyArray = (monthlyArrayUnknown as any[]) ?? [];
                monthKeysDesc.forEach((monthKeyEntry, index) => {
                    const cell = monthlyCellsByService[monthKeyEntry];
                    const [amount, isPaid, isArchived, ids] = coerceRow(monthlyArray[index]);
                    cell.amount = amount;
                    cell.is_paid = Boolean(isPaid);
                    cell.is_archived = Boolean(isArchived);
                    cell.ids = (ids as Array<number | string>).map(convertValueToACertainString);
                })

                const atonAccountsRaw =
                    (serviceAton[String(clientId)] && serviceAton[String(clientId)][String(serviceId)]) ??
                    (serviceAton[clientId] && serviceAton[clientId][serviceId]) ?? [];

                const aton_accounts = (atonAccountsRaw as Array<number | string>).map(convertValueToACertainString);

                const accountArchivedStatusRaw =
                    (serviceAtonStatus[String(clientId)] && serviceAtonStatus[String(clientId)][String(serviceId)]) ??
                    (serviceAtonStatus[clientId] && serviceAtonStatus[clientId][serviceId]) ?? {};

                const accountArchivedStatus: Record<number, boolean> = {};
                for (const [atonIdStringified, flag] of Object.entries(accountArchivedStatusRaw as Record<string, any>)) {
                    accountArchivedStatus[convertValueToACertainString(atonIdStringified)] = Boolean(flag);
                }

                servicesAggregated[serviceName] = {
                    ...monthlyCellsByService,
                    aton_accounts,
                    accountArchivedStatus,
                    serviceId,
                } as ServiceAggregatedRow;
            }

            table[printableName] = {
                client: { id: clientId },
                total: totalCellsByMonth,
                services: servicesAggregated,
            };
        });

        return table;
    };

    const clientsSortedByName = Object.values(clientDirectory).sort((
        firstClient,
        secondClient
    ) => {
        const firstClientName = (firstClient.last_name + " " + firstClient.first_name).trim() || `Клиент ${firstClient.id}`;
        const secondClientName = (secondClient.last_name + " " + secondClient.first_name).trim() || `Клиент ${secondClient.id}`;
        return firstClientName.localeCompare(secondClientName, "ru-RU-u-kf-upper");
    });

    const detailedTableActive = buildDetailedTableForClients(
        clientsSortedByName.filter((clientEntry) => !archivedByClientId[clientEntry.id])
    );

    const detailedTableArchived = buildDetailedTableForClients(
        clientsSortedByName.filter((clientEntry) => archivedByClientId[clientEntry.id])
    );

    const summaryDetailedTable = buildDetailedTableForClients(
        clientsSortedByName
    );

    return {
        summaryDetailedTable,
        detailedTableActive,
        detailedTableArchived,
        monthKeysDesc,
        tables: compressed.tables,
        clientDirectory,
    };
};

type PrintServicesResponse = {
    result?: string;
    meta?: { path?: string; message?: string };
};

export const handlePrintResponse = (
    clientId: number | string,
    callbackOnSuccess: (response: PrintServicesResponse) => void,
    callbackOnFail: (response: PrintServicesResponse) => void
) => {
    Api.get(
        'crm-services', `print?data[client_id]=${clientId}`
    ).all((response: PrintServicesResponse) => {
        if (response.result === 'success') {
            const filePath = process.env.REACT_APP_API_URL + (response.meta?.path ?? '');
            const link = document.createElement('a');
            link.href = filePath;
            link.download = filePath.split('/').pop() ?? 'report';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            callbackOnSuccess(response);
        } else {
            console.error('Ошибка при генерации файла:', response.meta?.message || 'Неизвестная ошибка');
            callbackOnFail(response);
        }
    })
}

/**
 * @param clientId
 * @param callbackOnFinally вызывается всегда (успех и ошибка), когда запрос реально
 * завершился — в отличие от результата внутри тела (`response.result === 'success'`),
 * который влияет только на то, скачивать файл или залогировать ошибку. Нужен
 * вызывающему коду, чтобы гарантированно снять индикатор загрузки и обновить
 * данные независимо от исхода.
 */
export const printAtonExcelFile = (
    clientId: number | string,
    callbackOnFinally?: () => void,
) => Api.get(
    'crm-services', `print?data[client_id]=${clientId}`
).all(
    (response: PrintServicesResponse & { meta?: { message?: string } }) => {
        if (response.result === 'success') {
            downloadExcelFile(response as Parameters<typeof downloadExcelFile>[0], true);
        } else {
            console.error('Ошибка при генерации файла:', response.meta?.message || 'Неизвестная ошибка');
        }
    },
    undefined,
    callbackOnFinally
)

export type TrustReportProgressStatus = 'processing' | 'finished' | 'failed';

export interface TrustReportProgressEntry {
    job_id: string;
    client_id: number;
    service_id: number;
    tariff_name: string;
    done: number;
    total: number;
    percent: number;
    status: TrustReportProgressStatus;
    error_message: string | null;
}

export interface TrustReportPrintStatusResponse {
    client_id: number;
    services: TrustReportProgressEntry[];
    task_index: number;
    tasks_queued: number;
    overall_percent: number;
    overall_status: TrustReportProgressStatus;
    generated_file: string | null;
}

/**
 * HTTP-пуллинг прогресса асинхронной генерации периодов/платежей ДУ (см.
 * FRONTEND_TRUST_MANAGEMENT_REPORT_PROGRESS.md, раздел 3) — фоллбэк для
 * WebSocket-канала `crm-service-report-{env}-{role}-{clientId}` и единственный
 * источник прогресса для ролей, не авторизованных на этот приватный канал
 * (director и т.п., см. раздел 2 доки).
 */
export const getTrustReportPrintStatus = (
    clientId: number | string,
    callbackOnSuccess: (data: TrustReportPrintStatusResponse) => void
) => Api.get(
    'crm-services', `print-status?data[client_id]=${clientId}`
).all(
    (response: { meta?: { data?: TrustReportPrintStatusResponse } }) => {
        if (response.meta?.data) {
            callbackOnSuccess(response.meta.data);
        }
    }
);

export const getServicesOnConfirmationWindow = (
    clientId: string | number,
    callbackOnSuccess?: ApiSuccessHandler<Service[]>
) => {
    return Api.get('crm-services', 'index')
        .with('clients', (query: LaravelQueryBuilder) =>
            query.orderBy('created_at').where('client_id', '=', clientId)
        ).whereHas('clients', (query: LaravelQueryBuilder) =>
            query.where('client_id', '=', clientId)
        ).where('is_archived', '=', false).all((response: ApiResponse<Service[]>) => {
            callbackOnSuccess && callbackOnSuccess(response);
            // setServices(res.data);
        });
}